<script setup lang="ts">
import { computed, mergeProps, useAttrs } from 'vue';

type IconTooltipButtonIconProps = {
  iconName?: string;
  iconColor?: string;
  iconSize?: string | number;
  iconClass?: string;
  delete?: boolean;
  edit?: boolean;
  /** @deprecated Use `iconName`. */
  icon?: string;
  /** @deprecated Use `iconColor`. */
  color?: string;
  /** @deprecated Use `iconSize`. Historically this prop sized the icon. */
  size?: string | number;
};

type TooltipLocation = 'top' | 'bottom' | 'start' | 'end' | 'left' | 'right' | 'center';

type IconTooltipButtonTooltipProps = (
  | {
      buttonTooltip: string;
      /** @deprecated Use `buttonTooltip`. */
      tooltip?: string;
    }
  | {
      buttonTooltip?: string;
      /** @deprecated Use `buttonTooltip`. */
      tooltip: string;
    }
) & {
  tooltipLocation?: TooltipLocation;
  tooltipDisabled?: boolean;
};

type IconTooltipButtonButtonProps = IconTooltipButtonTooltipProps & {
  buttonDisabled?: boolean | null;
  buttonLoading?: boolean | null;
  buttonSize?: string | number;
  buttonDensity?: 'default' | 'comfortable' | 'compact' | null;
  /** @deprecated Use `buttonDisabled`. */
  disabled?: boolean | null;
  /** @deprecated Use `buttonLoading`. */
  loading?: boolean | null;
};

defineOptions({ inheritAttrs: false });

const {
  buttonDensity = 'compact',
  buttonDisabled = null,
  buttonLoading = null,
  buttonSize,
  buttonTooltip,
  color,
  delete: isDelete = false,
  disabled = null,
  edit = false,
  icon,
  iconClass = '',
  iconColor,
  iconName,
  iconSize,
  loading = null,
  size,
  tooltip,
  tooltipDisabled = false,
  tooltipLocation = 'left'
} = defineProps<IconTooltipButtonButtonProps & IconTooltipButtonIconProps>();

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const attrs = useAttrs();

const resolvedButtonTooltip = computed(() => buttonTooltip ?? tooltip);
const resolvedButtonDisabled = computed(() => buttonDisabled ?? disabled ?? false);
const resolvedButtonLoading = computed(() => buttonLoading ?? loading ?? false);
const resolvedIconSize = computed(() => iconSize ?? size ?? 'small');

const resolvedIconColor = computed(() => {
  const explicitColor = iconColor ?? color;
  if (explicitColor) {
    return explicitColor;
  }
  if (isDelete) {
    return 'error';
  }

  return 'secondary';
});

const resolvedIconName = computed(() => {
  const explicitIconName = iconName ?? icon;
  if (explicitIconName) {
    return explicitIconName.replace('mdi-', '');
  }
  if (isDelete) {
    return 'delete-outline';
  }
  if (edit) {
    return 'pencil';
  }

  return 'information-outline';
});
</script>

<template>
  <v-tooltip
    :eager="false"
    :disabled="tooltipDisabled"
    :location="tooltipLocation"
    :text="resolvedButtonTooltip">
    <template #activator="{ props: tooltipActivatorProps }">
      <v-btn
        :aria-label="resolvedButtonTooltip"
        v-bind="mergeProps(tooltipActivatorProps, attrs)"
        variant="text"
        icon
        :density="buttonDensity"
        :size="buttonSize"
        :disabled="resolvedButtonDisabled"
        :loading="resolvedButtonLoading"
        :icon-attr="resolvedIconName"
        :tooltip-attr="resolvedButtonTooltip"
        @click.stop="emit('click', $event)">
        <v-icon
          v-show="!resolvedButtonLoading"
          :class="iconClass"
          :size="resolvedIconSize"
          :color="resolvedIconColor"
          :icon="`mdi-${resolvedIconName}`" />
      </v-btn>
    </template>
  </v-tooltip>
</template>
